import { useTranslation } from 'react-i18next';
import { GatorMarkCircle } from './GatorMarkCircle.js';
import { cn } from '../../lib/utils.js';

/**
 * THE loading indicator of the app — one visual, used everywhere.
 *
 * Centerpiece: the Gator mark inside a spinning accent arc with a soft
 * breathing halo, optional italic message below (brand voice, like the
 * wizard speech bubbles).
 *
 * Two ways to use it:
 *  - `<AppLoader />`            → inline, for page-level loading states.
 *  - `<AppLoaderOverlay />`     → fixed, full-viewport backdrop (blur + dim)
 *                                 that covers all content; used by the global
 *                                 loader and long-running actions.
 */
export function AppLoader({
  message,
  size = 'md',
  className,
}: {
  message?: string | null;
  size?: 'sm' | 'md';
  className?: string;
}) {
  const { t } = useTranslation();
  const label = message ?? t('common.loading', { defaultValue: 'Loading…' });
  const box = size === 'sm' ? 'h-16 w-16' : 'h-24 w-24';
  const mark = size === 'sm' ? 'h-9 w-9' : 'h-14 w-14';

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn('flex min-h-[40vh] flex-col items-center justify-center gap-5', className)}
    >
      <div className={cn('relative flex items-center justify-center', box)}>
        {/* Breathing halo */}
        <span
          aria-hidden
          className="absolute inset-0 rounded-full bg-ig-accent/10 motion-safe:animate-ping motion-safe:[animation-duration:2.2s]"
        />
        {/* Spinning accent arc */}
        <span
          aria-hidden
          className="absolute inset-0 rounded-full border-2 border-ig-accent/15 border-t-ig-accent motion-safe:animate-spin motion-safe:[animation-duration:1.1s]"
        />
        {/* Counter-rotating inner arc for depth */}
        <span
          aria-hidden
          className="absolute inset-2 rounded-full border border-transparent border-b-ig-accent/40 motion-safe:animate-spin motion-safe:[animation-duration:1.8s] motion-safe:[animation-direction:reverse]"
        />
        <GatorMarkCircle className={cn('relative', mark)} alt="" />
      </div>
      <p className="max-w-[320px] text-center font-display text-[15px] italic leading-[1.6] text-ig-text-secondary">
        {label}
      </p>
    </div>
  );
}

/** Full-viewport variant: dims and blurs everything behind it. */
export function AppLoaderOverlay({ message }: { message?: string | null }) {
  return (
    // --ig-bg-overlay is the theme's dedicated dimming color (with baked-in
    // alpha). Opacity modifiers like bg-ig-bg-primary/70 do NOT work here —
    // the ig tokens are plain CSS vars without <alpha-value> support, so
    // Tailwind drops the class and the backdrop turns fully transparent.
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-ig-bg-overlay backdrop-blur-sm"
      aria-modal="true"
    >
      <AppLoader message={message} className="min-h-0" />
    </div>
  );
}
